import { useEffect, useState } from "react";
import axios from "axios";
import { Trash2, UserPlus, Mail, Pencil, X, Globe, AlertTriangle } from "lucide-react";
import { API, ROLE_LABELS, ROLE_BADGE, formatError } from "./constants";
import { WhatsAppCard } from "./WhatsAppCard";
import { refreshSiteSettings } from "@/hooks/useSiteSettings";

const emptyMember = { name: "", email: "", password: "", role: "staff" };

const SITE_FIELDS = [
  { key: "phone", label: "Phone (as shown on site)", placeholder: "+91 80504 44041" },
  { key: "whatsapp", label: "WhatsApp number (country code, digits only)", placeholder: "918050444041" },
  { key: "email", label: "Public email", placeholder: "info@urbanveera.com" },
  { key: "instagram", label: "Instagram URL", placeholder: "https://instagram.com/urbanveera" },
  { key: "facebook", label: "Facebook URL", placeholder: "https://facebook.com/urbanveera" },
  { key: "linkedin", label: "LinkedIn URL", placeholder: "https://linkedin.com/company/urbanveera" },
  { key: "youtube", label: "YouTube URL", placeholder: "https://youtube.com/@urbanveera" },
];

const emptySite = { phone: "", whatsapp: "", email: "", instagram: "", facebook: "", linkedin: "", youtube: "" };

export const TeamSettings = ({ user }) => {
  const [members, setMembers] = useState([]);
  const [memberForm, setMemberForm] = useState(emptyMember);
  const [addingMember, setAddingMember] = useState(false);
  const [memberError, setMemberError] = useState("");
  const [alertEmail, setAlertEmail] = useState("");
  const [settingsMsg, setSettingsMsg] = useState("");
  const [siteForm, setSiteForm] = useState(emptySite);
  const [siteMsg, setSiteMsg] = useState("");
  const [savingSite, setSavingSite] = useState(false);
  const [resetMsg, setResetMsg] = useState("");
  const [resetting, setResetting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const [editError, setEditError] = useState("");
  const [editMsg, setEditMsg] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchMembers = () => {
    axios
      .get(`${API}/users`, { withCredentials: true })
      .then((res) => setMembers(res.data))
      .catch(() => setMembers([]));
  };

  useEffect(() => {
    fetchMembers();
    axios
      .get(`${API}/settings`, { withCredentials: true })
      .then((res) => {
        setAlertEmail(res.data.alert_email || "");
        setSiteForm({ ...emptySite, ...(res.data.site || {}) });
      })
      .catch(() => {});
  }, []);

  const addMember = async (e) => {
    e.preventDefault();
    setAddingMember(true);
    setMemberError("");
    try {
      await axios.post(`${API}/users`, memberForm, { withCredentials: true });
      setMemberForm(emptyMember);
      fetchMembers();
    } catch (err) {
      setMemberError(formatError(err.response?.data?.detail));
    } finally {
      setAddingMember(false);
    }
  };

  const deleteMember = async (id) => {
    if (!window.confirm("Remove this team member? Their assigned leads become unassigned.")) return;
    try {
      await axios.delete(`${API}/users/${id}`, { withCredentials: true });
      fetchMembers();
    } catch {
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    setEditError("");
    setEditMsg("");
    try {
      const payload = { name: editForm.name, email: editForm.email, role: editForm.role };
      if (editForm.password) payload.password = editForm.password;
      await axios.patch(`${API}/users/${editing.id}`, payload, { withCredentials: true });
      setEditMsg("Saved. New login details apply from the next sign-in.");
      setEditing(null);
      fetchMembers();
    } catch (err) {
      setEditError(formatError(err.response?.data?.detail));
    } finally {
      setSavingEdit(false);
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSettingsMsg("");
    try {
      await axios.post(`${API}/settings`, { alert_email: alertEmail }, { withCredentials: true });
      setSettingsMsg("Saved. New enquiry alerts will go to this inbox.");
    } catch (err) {
      setSettingsMsg(formatError(err.response?.data?.detail));
    }
  };

  const saveSite = async (e) => {
    e.preventDefault();
    setSavingSite(true);
    setSiteMsg("");
    try {
      await axios.post(`${API}/settings`, { site: siteForm }, { withCredentials: true });
      refreshSiteSettings();
      setSiteMsg("Saved. The website, social icons and the WhatsApp AI now use these details.");
    } catch (err) {
      setSiteMsg(formatError(err.response?.data?.detail));
    } finally {
      setSavingSite(false);
    }
  };

  const resetData = async () => {
    if (!window.confirm("This permanently deletes ALL leads, properties, change requests and chat history. Team logins and settings are kept. Continue?")) return;
    if (!window.confirm("Are you absolutely sure? This cannot be undone.")) return;
    setResetting(true);
    setResetMsg("");
    try {
      const res = await axios.post(`${API}/admin/reset-data`, {}, { withCredentials: true });
      const d = res.data.deleted || {};
      setResetMsg(`Cleared: ${d.leads || 0} leads, ${d.properties || 0} properties, ${d.change_requests || 0} requests, ${(d.chat_messages || 0) + (d.wa_messages || 0)} chat messages.`);
    } catch (err) {
      setResetMsg(formatError(err.response?.data?.detail));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="mt-6 grid grid-cols-1 items-start gap-6 lg:grid-cols-2" data-testid="team-view">
      <div className="rounded-xl border border-border bg-white p-6 shadow-soft" data-testid="team-section">
        <div className="flex items-center gap-2">
          <UserPlus size={16} className="text-amberdark" />
          <h2 className="font-display text-lg font-extrabold tracking-tight text-navy">Team & Access</h2>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Co-founders see and control everything. Managers see all leads and team performance. Staff see only their assigned work.
        </p>

        <ul className="mt-5 space-y-2" data-testid="members-list">
          {editMsg && (
            <li className="rounded-md border border-teal-200 bg-teal-50 px-4 py-2.5 text-sm font-semibold text-teal-700" data-testid="edit-member-success">{editMsg}</li>
          )}
          {members.map((m, i) => (
            <li key={m.id} className="flex items-center justify-between gap-3 rounded-md border border-border px-4 py-2.5" data-testid="member-row">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-navy">
                  {m.name}
                  <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${ROLE_BADGE[m.role] || ROLE_BADGE.staff}`}>
                    {ROLE_LABELS[m.role] || "Staff"}
                  </span>
                </p>
                <p className="truncate text-xs text-gray-500">{m.email}</p>
              </div>
              {m.id !== user.id && (
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    onClick={() => {
                      setEditing(m);
                      setEditForm({ name: m.name, email: m.email, password: "", role: m.role });
                      setEditError("");
                      setEditMsg("");
                    }}
                    data-testid={`member-edit-btn-${i}`}
                    aria-label={`Edit ${m.name}`}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-gray-400 transition-colors duration-200 hover:border-amber hover:bg-amber/10 hover:text-amberdark"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={() => deleteMember(m.id)}
                    data-testid={`member-delete-btn-${i}`}
                    aria-label={`Remove ${m.name}`}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-gray-400 transition-colors duration-200 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>

        <form onSubmit={addMember} className="mt-5 space-y-4 border-t border-border pt-5" data-testid="add-member-form">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input
              data-testid="member-name-input"
              required
              value={memberForm.name}
              onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
              placeholder="Member name"
              className="field-input"
            />
            <input
              data-testid="member-email-input"
              required
              type="email"
              value={memberForm.email}
              onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
              placeholder="Member email"
              className="field-input"
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input
              data-testid="member-password-input"
              required
              minLength={6}
              value={memberForm.password}
              onChange={(e) => setMemberForm({ ...memberForm, password: e.target.value })}
              placeholder="Temporary password (min 6)"
              className="field-input"
            />
            <select
              data-testid="member-role-select"
              value={memberForm.role}
              onChange={(e) => setMemberForm({ ...memberForm, role: e.target.value })}
              className="field-input"
            >
              <option value="staff">Staff</option>
              <option value="manager">Manager</option>
              <option value="cofounder">Co-founder</option>
            </select>
          </div>
          {memberError && (
            <p className="text-sm font-semibold text-red-600" data-testid="add-member-error">{memberError}</p>
          )}
          <button
            type="submit"
            data-testid="add-member-submit"
            disabled={addingMember}
            className="rounded-md bg-navy px-6 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-amber hover:text-navy disabled:opacity-50"
          >
            {addingMember ? "Adding..." : "Add Member"}
          </button>
        </form>
      </div>

      <div className="rounded-xl border border-border bg-white p-6 shadow-soft" data-testid="settings-section">
        <div className="flex items-center gap-2">
          <Mail size={16} className="text-amberdark" />
          <h2 className="font-display text-lg font-extrabold tracking-tight text-navy">Lead Alert Inbox</h2>
        </div>
        <p className="mt-1 text-xs text-gray-500">Every new website enquiry is emailed to this address instantly.</p>
        <form onSubmit={saveSettings} className="mt-5 space-y-4" data-testid="settings-form">
          <input
            data-testid="alert-email-input"
            required
            type="email"
            value={alertEmail}
            onChange={(e) => setAlertEmail(e.target.value)}
            placeholder="you@veeragroup.in"
            className="field-input"
          />
          <button
            type="submit"
            data-testid="save-settings-btn"
            className="rounded-md bg-amber px-6 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-navy transition-colors duration-300 hover:bg-amberdark hover:text-white"
          >
            Save Inbox
          </button>
          {settingsMsg && (
            <p className="text-sm font-semibold text-teal-700" data-testid="settings-message">{settingsMsg}</p>
          )}
        </form>
        <div className="mt-6 rounded-md bg-cream p-4 text-xs leading-relaxed text-gray-600">
          Tip: share the staff login with your team. Assign each lead from the
          "Assigned To" column in Leads and it appears in that person's view.
        </div>
      </div>

      <div className="rounded-xl border border-border bg-white p-6 shadow-soft" data-testid="site-settings-section">
        <div className="flex items-center gap-2">
          <Globe size={16} className="text-amberdark" />
          <h2 className="font-display text-lg font-extrabold tracking-tight text-navy">Site & Socials</h2>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          These power the website header, contact section, footer, social icons and the
          WhatsApp AI's answers. Leave a social blank to hide its icon — add it any time.
        </p>
        <form onSubmit={saveSite} className="mt-5 space-y-4" data-testid="site-settings-form">
          {SITE_FIELDS.map((f) => (
            <div key={f.key}>
              <label htmlFor={`site-${f.key}`} className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">
                {f.label}
              </label>
              <input
                id={`site-${f.key}`}
                data-testid={`site-${f.key}-input`}
                value={siteForm[f.key]}
                onChange={(e) => setSiteForm({ ...siteForm, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                className="field-input mt-2"
              />
            </div>
          ))}
          <button
            type="submit"
            data-testid="save-site-settings-btn"
            disabled={savingSite}
            className="rounded-md bg-amber px-6 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-navy transition-colors duration-300 hover:bg-amberdark hover:text-white disabled:opacity-50"
          >
            {savingSite ? "Saving..." : "Save Site Details"}
          </button>
          {siteMsg && (
            <p className="text-sm font-semibold text-teal-700" data-testid="site-settings-message">{siteMsg}</p>
          )}
        </form>
      </div>

      <WhatsAppCard />

      <div className="rounded-xl border border-red-200 bg-red-50/40 p-6 lg:col-span-2" data-testid="danger-zone">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-red-600" />
          <h2 className="font-display text-lg font-extrabold tracking-tight text-navy">Reset Workspace Data</h2>
        </div>
        <p className="mt-1 max-w-2xl text-xs leading-relaxed text-gray-500">
          Permanently deletes every lead, property, change request and chat message —
          use this to clear old sample data before going live. Team logins, site
          details and settings are kept.
        </p>
        <button
          onClick={resetData}
          data-testid="reset-data-btn"
          disabled={resetting}
          className="mt-4 rounded-md border-2 border-red-600 px-6 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-red-600 transition-colors duration-300 hover:bg-red-600 hover:text-white disabled:opacity-50"
        >
          {resetting ? "Clearing..." : "Clear All Data"}
        </button>
        {resetMsg && (
          <p className="mt-3 text-sm font-semibold text-teal-700" data-testid="reset-data-message">{resetMsg}</p>
        )}
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-navy/60 p-4 backdrop-blur-sm"
          data-testid="edit-member-modal"
          onClick={() => setEditing(null)}
        >
          <div
            className="relative max-h-[85vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-8 shadow-lift"
            data-lenis-prevent="true"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setEditing(null)}
              data-testid="edit-member-cancel"
              aria-label="Close edit member form"
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-md border border-border text-gray-500 transition-colors duration-300 hover:border-navy hover:text-navy"
            >
              <X size={16} />
            </button>
            <h3 className="font-display text-2xl font-extrabold tracking-tight text-navy" data-testid="edit-member-heading">
              Edit {editing.name}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-gray-600">
              Change their login email or reset their password. Leave password
              blank to keep the current one.
            </p>
            <form onSubmit={saveEdit} className="mt-7 space-y-5" data-testid="edit-member-form">
              <div>
                <label htmlFor="edit-name" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Name</label>
                <input id="edit-name" data-testid="edit-name-input" required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="field-input mt-2" />
              </div>
              <div>
                <label htmlFor="edit-email" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Login email</label>
                <input id="edit-email" data-testid="edit-email-input" required type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="field-input mt-2" />
              </div>
              <div>
                <label htmlFor="edit-password" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">New password <span className="normal-case tracking-normal text-gray-400">(optional)</span></label>
                <input id="edit-password" data-testid="edit-password-input" minLength={6} value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Leave blank to keep current" className="field-input mt-2" />
              </div>
              <div>
                <label htmlFor="edit-role" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Role</label>
                <select id="edit-role" data-testid="edit-role-select" value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} className="field-input mt-2">
                  <option value="staff">Staff</option>
                  <option value="manager">Manager</option>
                  <option value="cofounder">Co-founder</option>
                </select>
              </div>
              {editError && (
                <p className="text-sm font-semibold text-red-600" data-testid="edit-member-error">{editError}</p>
              )}
              <button
                type="submit"
                data-testid="edit-member-submit"
                disabled={savingEdit}
                className="w-full rounded-md bg-amber px-8 py-4 text-sm font-bold text-navy transition-colors duration-300 hover:bg-amberdark hover:text-white disabled:opacity-50"
              >
                {savingEdit ? "Saving..." : "Save Changes"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
